import { useMemo, useState, type ReactNode } from 'react'
import { PackageOpen, Trash2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { PoCustomFieldDef } from '@/types/purchaseOrders'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAllMaterials } from '@/hooks/useInventoryMaster'
import { useFetchPoPrefill } from '@/hooks/usePurchaseOrders'
import { formatMoney } from '@/lib/poTotals'
import { ShipToPicker, VendorPicker } from './PoPartyPickers'
import { TermsCard } from './PoTermsCard'
import { cn } from '@/lib/utils'
import {
  INCOTERMS,
  draftLineFromPrefill,
  draftTotals,
  emptyDraftLine,
  type PoDraft,
  type PoDraftLine,
  type PoDraftShipTo,
} from '@/lib/purchaseOrderDraft'

interface PurchaseOrderFormProps {
  draft: PoDraft
  onChange: (next: PoDraft) => void
  /** The template's custom fields, rendered as inputs under Order details. */
  customFieldDefs?: PoCustomFieldDef[]
}

type TextKey = Exclude<keyof PoDraft, 'shipTo' | 'lines' | 'customFields' | 'terms'>

const SHIP_TO_FIELDS: { key: keyof PoDraftShipTo; label: string; wide?: boolean }[] = [
  { key: 'name', label: 'Name', wide: true },
  { key: 'street', label: 'Street', wide: true },
  { key: 'postcode', label: 'Postcode' },
  { key: 'city', label: 'City' },
  { key: 'country', label: 'Country' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email', wide: true },
]

/** Suggestions only: each list backs a datalist, so any other wording is still accepted. */
const PAYMENT_TERMS = ['Net 15', 'Net 30', 'Net 45', 'Net 60', 'Due on receipt', '2/10 Net 30']
const SHIPPING_METHODS = ['Courier', 'Road freight', 'Air freight', 'Sea freight', 'Cold chain courier', 'Collection']

const LINE_COLUMNS: { label: string; className?: string }[] = [
  { label: '#', className: 'w-10 text-center' },
  { label: 'Item no.' },
  { label: 'Supplier ref' },
  { label: 'Description' },
  { label: 'Qty', className: 'text-right' },
  { label: 'UoM' },
  { label: 'Unit price', className: 'text-right' },
  { label: 'Disc %', className: 'text-right' },
  { label: 'Tax %', className: 'text-right' },
  { label: 'Amount', className: 'text-right' },
  { label: '', className: 'w-10' },
]

/** A white bordered card with a small uppercase heading, the editor's building block. */
export function PoCard({
  title,
  action,
  children,
  className,
  bodyClassName,
}: {
  title: string
  action?: ReactNode
  children: ReactNode
  className?: string
  bodyClassName?: string
}) {
  return (
    <section className={cn('rounded-xl border border-slate-200 bg-white shadow-sm', className)}>
      <div className='flex min-h-[44px] flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-2'>
        <h3 className='text-xs font-semibold uppercase tracking-wide text-slate-500'>{title}</h3>
        {action}
      </div>
      <div className={cn('p-4', bodyClassName)}>{children}</div>
    </section>
  )
}

/**
 * The body of the purchase-order editor (z8nrz7f7dz), laid out like an ERP
 * order entry: vendor and ship-to, order details, the line items and notes in
 * the main column; the order summary in a
 * sticky sidebar. Controlled — the dialog around it owns the draft and decides
 * what saving means (create, or a revision of a sent order).
 */
export function PurchaseOrderForm({ draft, onChange, customFieldDefs = [] }: PurchaseOrderFormProps) {
  const set = (key: TextKey, value: string) => onChange({ ...draft, [key]: value })
  const setShipTo = (key: keyof PoDraftShipTo, value: string) =>
    onChange({ ...draft, shipTo: { ...draft.shipTo, [key]: value } })
  const setLine = (key: string, patch: Partial<PoDraftLine>) =>
    onChange({ ...draft, lines: draft.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)) })
  const removeLine = (key: string) => onChange({ ...draft, lines: draft.lines.filter((l) => l.key !== key) })

  const totals = draftTotals(draft)
  const orderRate = draft.taxRatePct.trim() === '' ? 0 : Number(draft.taxRatePct)
  const currency = draft.currency.trim() || undefined

  const field = (
    key: TextKey,
    label: string,
    props: React.ComponentProps<typeof Input> = {},
    wrapperClassName?: string,
  ) => (
    <div className={cn('space-y-1', wrapperClassName)}>
      <Label htmlFor={`po-${key}`} className='text-xs font-medium text-slate-600'>
        {label}
      </Label>
      <Input
        id={`po-${key}`}
        value={draft[key]}
        onChange={(e) => set(key, key === 'currency' ? e.target.value.toUpperCase() : e.target.value)}
        {...props}
        className={cn('h-9', props.className)}
      />
    </div>
  )

  return (
    <div className='grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_320px]'>
      <div className='min-w-0 space-y-5'>
        <div className='grid gap-5 md:grid-cols-2'>
          <PoCard title='Vendor' action={<VendorPicker draft={draft} onChange={onChange} />}>
            <div className='grid grid-cols-2 gap-3'>
              {field('supplierName', 'Vendor name *', {}, 'col-span-2')}
              {field('supplierContact', 'Contact')}
              {field('supplierTaxId', 'Tax / VAT ID')}
              {field('supplierEmail', 'Email', { type: 'email' })}
              {field('supplierPhone', 'Phone')}
              {field('supplierAddress', 'Address', {}, 'col-span-2')}
            </div>
          </PoCard>
          <PoCard
            title='Ship to'
            action={<ShipToPicker shipTo={draft.shipTo} onChange={(shipTo) => onChange({ ...draft, shipTo })} />}
          >
            <div className='grid grid-cols-2 gap-3'>
              {SHIP_TO_FIELDS.map(({ key, label, wide }) => (
                <div key={key} className={cn('space-y-1', wide && 'col-span-2')}>
                  <Label htmlFor={`po-shipto-${key}`} className='text-xs font-medium text-slate-600'>
                    {label}
                  </Label>
                  <Input
                    id={`po-shipto-${key}`}
                    className='h-9'
                    value={draft.shipTo[key]}
                    onChange={(e) => setShipTo(key, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </PoCard>
        </div>

        <PoCard title='Order details'>
          <div className='grid grid-cols-2 gap-3 md:grid-cols-4'>
            {field('requestedDeliveryDate', 'Requested delivery', { type: 'date' })}
            {/* Datalists, not Selects: the common values are offered, but a
                supplier's own wording ("DAP Leuven", "Net 30 EOM") is still accepted. */}
            {field('paymentTerms', 'Payment terms', { list: 'po-payment-terms', placeholder: 'e.g. Net 30' })}
            {field('incoterms', 'Incoterms 2020', { list: 'po-incoterms', placeholder: 'e.g. DAP' })}
            {field('shippingMethod', 'Shipping method', { list: 'po-shipping-methods', placeholder: 'e.g. Courier' })}
            {field('currency', 'Currency', { maxLength: 3, className: 'uppercase', placeholder: 'EUR' })}
            {field('buyer', 'Buyer')}
            {field('reference', 'Reference / quote no.')}
            {field('taxRatePct', 'VAT %', { type: 'number', min: 0, step: 'any', className: 'text-right tabular-nums' })}
            <datalist id='po-payment-terms'>
              {PAYMENT_TERMS.map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
            <datalist id='po-incoterms'>
              {INCOTERMS.map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
            <datalist id='po-shipping-methods'>
              {SHIPPING_METHODS.map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
          </div>

          {customFieldDefs.length > 0 && (
            <div className='mt-5 space-y-3 border-t border-slate-100 pt-4'>
              <h4 className='text-xs font-semibold uppercase tracking-wide text-slate-500'>Additional details</h4>
              <div className='grid grid-cols-2 gap-3 md:grid-cols-4'>
                {customFieldDefs.map((f) => (
                  <CustomFieldInput
                    key={f.key}
                    def={f}
                    value={draft.customFields[f.key] ?? ''}
                    onChange={(v) => onChange({ ...draft, customFields: { ...draft.customFields, [f.key]: v } })}
                  />
                ))}
              </div>
              {customFieldDefs.some((f) => f.required) && (
                <p className='text-xs text-slate-500'>Fields marked * must be filled in before the order can be sent.</p>
              )}
            </div>
          )}
        </PoCard>

        <PoCard
          title={`Line items${draft.lines.length > 0 ? ` (${draft.lines.length})` : ''}`}
          action={<AddMaterialPicker onAdd={(line) => onChange({ ...draft, lines: [...draft.lines, line] })} />}
          bodyClassName='p-0'
        >
          <div className='overflow-x-persistent'>
            <table className='w-full min-w-[920px] text-sm'>
              <thead>
                <tr className='border-b border-slate-200 bg-slate-50/80'>
                  {LINE_COLUMNS.map((c, i) => (
                    <th
                      key={i}
                      scope='col'
                      className={cn(
                        'whitespace-nowrap px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500',
                        c.className,
                      )}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className='divide-y divide-slate-100'>
                {draft.lines.map((l, i) => (
                  <tr key={l.key} className='align-middle hover:bg-slate-50/60'>
                    <td className='px-2 py-1.5 text-center text-xs font-medium tabular-nums text-slate-400'>{i + 1}</td>
                    {/* The material's code: set by the server, not typed. */}
                    <td className='whitespace-nowrap px-2 py-1.5 font-mono text-xs text-slate-600'>
                      {l.articleNo || '—'}
                    </td>
                    <LineInput line={l} k='supplierRef' onSet={setLine} className='w-28' />
                    <LineInput line={l} k='description' onSet={setLine} className='min-w-[200px]' />
                    <LineInput line={l} k='qty' onSet={setLine} className='w-20' numeric />
                    <LineInput line={l} k='uom' onSet={setLine} className='w-16' />
                    <LineInput line={l} k='unitPrice' onSet={setLine} className='w-24' numeric />
                    <LineInput line={l} k='discountPct' onSet={setLine} className='w-16' numeric />
                    <LineInput
                      line={l}
                      k='taxPct'
                      onSet={setLine}
                      className='w-16'
                      numeric
                      placeholder={String(orderRate)}
                    />
                    <td className='whitespace-nowrap px-2 py-1.5 text-right font-medium tabular-nums text-slate-900'>
                      {formatMoney(totals.lineTotals[i])}
                    </td>
                    <td className='px-2 py-1.5'>
                      <button
                        type='button'
                        title='Remove line'
                        aria-label={`Remove line ${i + 1}`}
                        className='flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600'
                        onClick={() => removeLine(l.key)}
                      >
                        <Trash2 className='h-4 w-4' />
                      </button>
                    </td>
                  </tr>
                ))}
                {draft.lines.length === 0 && (
                  <tr>
                    <td colSpan={LINE_COLUMNS.length} className='px-4 py-10 text-center'>
                      <PackageOpen className='mx-auto h-8 w-8 text-slate-300' />
                      <p className='mt-2 text-sm font-medium text-slate-700'>No line items yet</p>
                      <p className='text-xs text-slate-500'>Use Add material… above to add a line from the catalogue.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </PoCard>

        <PoCard title='Notes / special instructions'>
          <Label htmlFor='po-notes' className='sr-only'>
            Notes / special instructions
          </Label>
          <Textarea
            id='po-notes'
            rows={3}
            placeholder='Delivery instructions, handling requirements, references for the supplier…'
            value={draft.notes}
            onChange={(e) => set('notes', e.target.value)}
          />
        </PoCard>

        <TermsCard terms={draft.terms} onChange={(terms) => onChange({ ...draft, terms })} />
      </div>

      <aside className='space-y-5 lg:sticky lg:top-0'>
        <PoCard title='Order summary'>
          <dl className='space-y-2 text-sm' data-testid='po-totals'>
            <TotalRow
              label={`Subtotal · ${draft.lines.length} line${draft.lines.length === 1 ? '' : 's'}`}
              value={formatMoney(totals.subtotal, currency)}
            />
            <TotalRow
              label='Discounts'
              value={totals.discountTotal > 0 ? `− ${formatMoney(totals.discountTotal, currency)}` : formatMoney(0, currency)}
              muted
            />
            <TotalRow label={`VAT ${orderRate}%`} value={formatMoney(totals.tax, currency)} />
            <div className='flex items-center justify-between gap-4 text-slate-600'>
              <dt>
                <Label htmlFor='po-shippingCost' className='text-sm font-normal text-slate-600'>
                  Shipping
                </Label>
              </dt>
              <dd>
                <Input
                  id='po-shippingCost'
                  className='h-8 w-28 text-right tabular-nums'
                  type='number'
                  min={0}
                  step='any'
                  placeholder='0.00'
                  value={draft.shippingCost}
                  onChange={(e) => set('shippingCost', e.target.value)}
                />
              </dd>
            </div>
            <div className='mt-1 flex items-baseline justify-between gap-4 border-t border-slate-200 pt-3'>
              <dt className='text-sm font-semibold text-slate-900'>Grand total</dt>
              <dd className='text-lg font-semibold tabular-nums text-slate-900'>
                {formatMoney(totals.grandTotal, currency)}
              </dd>
            </div>
          </dl>
        </PoCard>
      </aside>
    </div>
  )
}

/** NONE: Radix Select forbids an empty item value, so "not chosen" needs a sentinel. */
const NONE = '__none__'

function CustomFieldInput({ def, value, onChange }: { def: PoCustomFieldDef; value: string; onChange: (v: string) => void }) {
  const id = `po-custom-${def.key}`
  return (
    <div className='space-y-1'>
      <Label htmlFor={id} className='text-xs font-medium text-slate-600'>
        {def.label}
        {def.required && <span className='ml-0.5 text-red-500'>*</span>}
      </Label>
      {def.type === 'select' ? (
        <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? '' : v)}>
          <SelectTrigger id={id} className='h-9'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>—</SelectItem>
            {(def.options ?? []).filter((o) => o.trim()).map((o) => (
              <SelectItem key={o} value={o}>
                {o}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Input
          id={id}
          className='h-9'
          type={def.type === 'number' ? 'number' : def.type === 'date' ? 'date' : 'text'}
          step={def.type === 'number' ? 'any' : undefined}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  )
}

function TotalRow({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className={cn('flex justify-between gap-4', muted ? 'text-slate-400' : 'text-slate-600')}>
      <dt>{label}</dt>
      <dd className='tabular-nums text-slate-900'>{value}</dd>
    </div>
  )
}

type LineKey = Exclude<keyof PoDraftLine, 'key' | 'materialId'>

function LineInput({
  line,
  k,
  onSet,
  className,
  numeric,
  placeholder,
}: {
  line: PoDraftLine
  k: LineKey
  onSet: (key: string, patch: Partial<PoDraftLine>) => void
  className?: string
  numeric?: boolean
  placeholder?: string
}) {
  return (
    <td className='px-1 py-1.5'>
      <Input
        className={cn('h-8 px-2', numeric && 'text-right tabular-nums', className)}
        type={numeric ? 'number' : 'text'}
        min={numeric ? 0 : undefined}
        step={numeric ? 'any' : undefined}
        aria-label={k}
        placeholder={placeholder}
        value={line[k]}
        onChange={(e) => onSet(line.key, { [k]: e.target.value })}
      />
    </td>
  )
}

/**
 * Search the catalogue and add a material as a new line. The line is seeded
 * from the material's prefill (supplier ref, unit, price) when the server has
 * one, and from the catalogue entry when it does not.
 */
function AddMaterialPicker({ onAdd }: { onAdd: (line: PoDraftLine) => void }) {
  const [open, setOpen] = useState(false)
  const { data } = useAllMaterials({}, { enabled: open })
  const fetchPrefill = useFetchPoPrefill()
  const materials = useMemo(() => data?.items ?? [], [data])
  const options = useMemo(
    () => materials.map((m) => ({ value: m.id, label: `${m.materialId} · ${m.materialName}` })),
    [materials],
  )

  async function add(id: string) {
    const m = materials.find((x) => x.id === id)
    try {
      onAdd(draftLineFromPrefill((await fetchPrefill(id)).line))
    } catch {
      onAdd({ ...emptyDraftLine(id), description: m?.materialName ?? '', articleNo: m?.materialCode ?? '' })
    }
  }

  return (
    <div className='w-full sm:w-72' onFocusCapture={() => setOpen(true)} onPointerDownCapture={() => setOpen(true)}>
      <SearchableSelect
        value=''
        onChange={(id) => void add(id)}
        options={options}
        placeholder='Add material…'
        emptyMessage={data ? 'No materials match.' : 'Loading materials…'}
      />
    </div>
  )
}
